import React, {
    FC,
    useState,
    useCallback,
    useMemo,
    FormEvent,
    useEffect
} from "react"
import { hasClassificationFieldSchema } from "resha"
import { Button } from "dash-styled"

import { SettingsSubSection } from "../Common/SettingsSubSection"
import { SettingsSection } from "../Common/SettingsSection"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import {
    DataPointsToUpdate,
    MappedJobStatus
} from "../../../../../../Models/API/APIJobsPerStatusModel"
import { AppTTLSettings } from "../../../../../../Models/API/AppSettings"
import { hasValue } from "../../../../../../Models/Client/FetchState"
import { useJobCount } from "../../../../../../Hooks/useJobCount"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { UpdateDataPointsModal } from "../Common/UpdateDataPointsModal"
import { EmailInbox } from "./EmailInbox/EmailInbox"
import { TTL } from "./TTL/TTL"
import { FormFieldsTTL } from "."

interface SettingsFormProps {
    appID: string
    initialValue: FormFieldsTTL
    onSave: (formFields: FormFieldsTTL) => Promise<void>
    onDirtinessChanged: (isDirty: boolean) => void
}

export const SettingsForm: FC<SettingsFormProps> = ({
    appID,
    initialValue,
    onSave,
    onDirtinessChanged
}) => {
    const [value, setValue] = useState(initialValue)
    const [shouldReset, allowReset, disallowReset] = useBoolean(true)
    const [
        showUpdateDataPointModal,
        showUpdateDataPointModalTrue,
        showUpdateDataPointModalFalse
    ] = useBoolean(false)

    const [ttlSettingTouched, setTTLSettingTouched] = useState(false)
    const isDirty = useMemo(
        () => JSON.stringify(value) !== JSON.stringify(initialValue),
        [value, initialValue]
    )
    const [isSaving, setSaving] = useState(false)

    const jobCount = useJobCount(appID)
    const instanceFetch = useAppInstance(appID)

    const triggerSave = useCallback(
        async (updateCheckboxes?: DataPointsToUpdate) => {
            try {
                setSaving(true)
                const payload: string[] = []
                if (updateCheckboxes) {
                    Object.keys(updateCheckboxes).forEach(item => {
                        if (updateCheckboxes[item as MappedJobStatus]) {
                            payload.push(item)
                        }
                    })
                }
                await onSave({
                    ...value,
                    cancelAndRelabelJobsInState: payload
                })
                allowReset()
            } finally {
                setSaving(false)
                showUpdateDataPointModalFalse()
            }
        },
        [allowReset, onSave, showUpdateDataPointModalFalse, value]
    )

    const handleTTLChange = useCallback(
        (ttlSettings: AppTTLSettings) => {
            setTTLSettingTouched(
                JSON.stringify(ttlSettings) !==
                    JSON.stringify(initialValue.ttlSettings)
            )
            disallowReset()
            setValue(f => ({ ...f, ttlSettings }))
        },
        [disallowReset, initialValue.ttlSettings]
    )

    const updateDataPointModalEnabled = ttlSettingTouched
    const handleSave = useCallback(
        async (e?: FormEvent) => {
            e?.preventDefault()

            if (updateDataPointModalEnabled) {
                showUpdateDataPointModalTrue()
            } else {
                triggerSave()
            }
        },
        [showUpdateDataPointModalTrue, triggerSave, updateDataPointModalEnabled]
    )

    useEffect(() => {
        if (shouldReset) {
            setValue(initialValue)
        }
    }, [initialValue, shouldReset])

    useEffect(() => {
        onDirtinessChanged(isDirty)
    }, [onDirtinessChanged, isDirty])

    return (
        <form onSubmit={handleSave}>
            {hasValue(instanceFetch) &&
                !hasClassificationFieldSchema(
                    instanceFetch?.value.outputUiSchema
                ) && (
                    <SettingsSection title="">
                        <SettingsSubSection
                            title="Email inbox"
                            testId="email-inbox-settings"
                        >
                            <EmailInbox appID={appID} />
                        </SettingsSubSection>
                    </SettingsSection>
                )}
            <SettingsSection title="">
                <SettingsSubSection title="Time to live" testId="ttl-settings">
                    <TTL value={value.ttlSettings} onChange={handleTTLChange} />
                </SettingsSubSection>
            </SettingsSection>
            <Button
                testId="settings-submit-button"
                type="submit"
                className="mt-7 mx-auto w-48"
                disabled={!isDirty || isSaving}
                onClick={handleSave}
            >
                {isSaving ? "Saving..." : "Save changes"}
            </Button>
            {showUpdateDataPointModal ? (
                <UpdateDataPointsModal
                    isSaving={isSaving}
                    jobCount={jobCount}
                    handleSave={triggerSave}
                    handleClose={showUpdateDataPointModalFalse}
                />
            ) : null}
        </form>
    )
}
